import { useEditor, EditorContent, Extension } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { useTheme, type FontSize } from "@repo/ui";
import { useStableCallback } from "@repo/utils/hooks/useStableCallback";
import { useInjectGlobalStyles, useStyles } from "purse-styles";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";

export type TextEditorRef = {
	getValue(): string;
	clear(): void;
	focus(): void;
	blur(): void;
	insertText(text: string): void;
};

type TextEditorProps = {
	initialValue?: string;
	placeholder?: string;
	style?: React.CSSProperties;
	onSubmit?: () => void;
	size?: FontSize;
	onBlur?: () => void;
	autoFocus?: boolean;
	["aria-label"]?: string;
	onChange?: (value: string) => void;
};

function createSubmitExtension(onSubmit: (() => void) | undefined) {
	return Extension.create({
		name: "submitOnModEnter",
		addKeyboardShortcuts() {
			return {
				"Mod-Enter": () => {
					if (onSubmit) {
						onSubmit();
						return true;
					}
					return false;
				},
			};
		},
	});
}

export const TextEditor = forwardRef<TextEditorRef, TextEditorProps>(
	function TextEditor(
		{
			initialValue = "",
			placeholder = "Leave a comment...",
			style,
			size = "prose",
			autoFocus,
			onSubmit,
			onBlur,
			"aria-label": ariaLabel,
			onChange,
		}: TextEditorProps,
		ref,
	) {
		const theme = useTheme();
		const stableOnSubmit = useStableCallback(onSubmit);
		const stableOnChange = useStableCallback(onChange);
		const stableOnBlur = useStableCallback(onBlur);

		const submitExtension = useMemo(
			() => createSubmitExtension(stableOnSubmit),
			[stableOnSubmit],
		);

		const editor = useEditor({
			extensions: [
				StarterKit.configure({
					// Disable rich text features for plain text editor
					blockquote: false,
					bold: false,
					bulletList: false,
					code: false,
					codeBlock: false,
					heading: false,
					horizontalRule: false,
					italic: false,
					link: false,
					listItem: false,
					orderedList: false,
					strike: false,
					underline: false,
				}),
				Placeholder.configure({ placeholder }),
				submitExtension,
			],
			content: initialValue,
			autofocus: autoFocus ? "end" : false,
			onUpdate: ({ editor }) => {
				stableOnChange?.(editor.getText());
			},
			onBlur: stableOnBlur,
			editorProps: {
				attributes: {
					...(ariaLabel ? { "aria-label": ariaLabel } : {}),
				},
			},
		});

		useImperativeHandle(
			ref,
			() => ({
				getValue: () => editor?.getText() ?? "",
				clear: () => {
					editor?.commands.clearContent();
				},
				focus: () => {
					editor?.commands.focus();
				},
				blur: () => {
					editor?.commands.blur();
				},
				insertText: (text: string) => {
					editor?.commands.insertContent(text);
				},
			}),
			[editor],
		);

		useEffect(() => {
			if (editor) {
				editor.extensionManager.extensions.forEach((ext) => {
					if (ext.name === "placeholder") {
						ext.options.placeholder = placeholder;
						editor.view.dispatch(editor.state.tr);
					}
				});
			}
		}, [editor, placeholder]);

		const fontStyle = theme.fontStyles[size];
		const editorStyles = useStyles({
			position: "relative",
			...fontStyle,
			fontFamily: theme.fontFamilies.Inter,
			color: theme.colors.sand12,
			outline: "none",
			cursor: "text",
			minWidth: 0,

			"& .tiptap": {
				outline: "none",
				minHeight: fontStyle.lineHeight,
			},

			"& .tiptap p": {
				margin: 0,
			},

			"& .tiptap::selection": {
				background: theme.colors.accentA4,
			},
		});

		useInjectGlobalStyles(
			`.tiptap p.is-editor-empty:first-child::before`,
			{
				content: "attr(data-placeholder)",
				color: theme.colors.sand10,
				pointerEvents: "none",
				float: "left",
				height: 0,
			},
			[theme.colors.sand10],
		);

		return (
			<div className={editorStyles} style={style}>
				<EditorContent editor={editor} />
			</div>
		);
	},
);
